import { GetOfferEdgeAPIResponse, GetOfferInProgressAPIResponse, disposalOptions } from '../interfaces/interface.offer';
import answerState from '../store/store.answers';
import { state as imageCaptureState } from '../store/store.image-capture';
import { state as supplementState } from '../store/store.supplement';
import { ANSWER_LEAD_TYPE_CASH_OFFER, ANSWER_LEAD_TYPE_OPT_IN, ANSWER_LEAD_TYPE_OPT_OUT, removeTypes } from '../utils/utils.answers';
import { jsonIf } from '../utils/utils.network';
import { KBYC_DEALER, cleanAnswersForAPI, removeInvalidAnswers, removeNotNeededAnswers } from '../utils/utils.offer';
import {
  QUESTION_ARE_QUESTIONS_VERIFIED,
  QUESTION_AUTOCHECK_DATA_JSON,
  QUESTION_CERTIFIED_CARS,
  QUESTION_HAS_FLOOD_DAMAGE,
  QUESTION_HAS_FRAME_DAMAGE,
  QUESTION_IS_AUTOCHECK_AUCTION_ELIGIBLE,
  QUESTION_IS_SALVAGE,
  QUESTION_LEAD_TYPE,
  QUESTION_MAKE,
  QUESTION_MAKE_MODEL,
  QUESTION_MODEL,
  QUESTION_NEW_CARS,
  QUESTION_NOT_ACTUAL_MILEAGE,
  QUESTION_ODORS,
  QUESTION_PIXALL_ID,
  QUESTION_PIXALL_REAL_ID,
  QUESTION_PREVIOUSLY_CANADIAN,
  QUESTION_TOGGLE_MODIFICATIONS,
  QUESTION_USED_CARS,
  QUESTION_VERIFIED_MILEAGE,
  QUESTION_YEAR_RANGE,
} from '../utils/utils.question';

export const fetchInProgressOffer = async (prospectId): Promise<GetOfferInProgressAPIResponse> => {
  return fetch(`${process.env.BASE_URL}/${process.env.V_PREFIX}/vehicle/prospectId/${prospectId}?a=b&api_key=${process.env.API_KEY}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
      QuestionConversion: 'fury', // TODO: Make this an env?
      Date: new Date().toUTCString(),
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const fetchOfferDetail = async (offerID): Promise<GetOfferEdgeAPIResponse> => {
  return fetch(`${process.env.BASE_URL}/${process.env.V_PREFIX}/offers/${offerID}?a=b&api_key=${process.env.API_KEY}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
      QuestionConversion: 'fury', // TODO: Make this an env?
      Date: new Date().toUTCString(),
    },
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

const gatherOtherAnswerData = () => {
  const qxOther = [
    {
      questionId: 'qx/HttpReferer',
      value: document.referrer,
    },
    {
      questionId: 'qx/AutotraderCarID',
      value: '',
    },
    {
      questionId: 'qx/LandingPage', // TODO: update value to take from prop
      value: '73111D1C-71E5-4563-8EED-03932B322D6C', // Hardcoded for MVP
    },
    {
      questionId: 'qx/Asset',
      value: '',
    },
    {
      questionId: 'qx/ExpCode',
      value: '',
    },
    {
      questionId: 'qx/ReturnerID',
      value: '',
    },
    {
      questionId: 'qx/ConsumerGenerated',
      value: !answerState.dealerInfo.authToken, // Hardcoded for MVP
    },
    {
      questionId: 'qx/UserComments',
    },
    {
      questionId: 'qx/ConsumerCommunicationMethod',
      value: answerState.contactInfo.emailCustomerCheckbox ? 'BOTH' : '',
    },
    {
      questionId: 'qx/AnswerSourcesInput',
      value: [{ questionCode: 'DealerModifiedAccidentQuestion', value: false }],
    },
    {
      questionId: 'qx/OfferCode',
      value: answerState.other['qx/OfferCode'].value,
    },
  ];

  if (answerState.dealerInfo?.dealerGuid && answerState.dealerInfo?.dealerGuid !== '00000000-0000-0000-0000-000000000000') {
    qxOther.push({
      questionId: 'qx/SiteReferralUserID',
      value: answerState.dealerInfo.dealerGuid,
    });
  }

  const removeQuestionIds = [
    QUESTION_HAS_FLOOD_DAMAGE,
    QUESTION_HAS_FRAME_DAMAGE,
    QUESTION_IS_SALVAGE,
    QUESTION_PREVIOUSLY_CANADIAN,
    QUESTION_NOT_ACTUAL_MILEAGE,
    QUESTION_IS_AUTOCHECK_AUCTION_ELIGIBLE,
    QUESTION_AUTOCHECK_DATA_JSON,
  ];

  const cleanOther = removeNotNeededAnswers([...cleanAnswersForAPI(answerState.other)], removeQuestionIds);
  if (!answerState.pixall.includePixall) {
    qxOther.push({
      questionId: QUESTION_PIXALL_ID,
      value: '',
    });
    qxOther.push({
      questionId: QUESTION_PIXALL_REAL_ID,
      value: '',
    });
  }
  if (answerState.other[QUESTION_ARE_QUESTIONS_VERIFIED].value === 0) {
    qxOther.push({
      questionId: QUESTION_VERIFIED_MILEAGE,
      value: '',
    });
  }
  const otherAnswers = [...cleanOther, ...qxOther];

  // some of this is hard coded in the current funnel
  // TODO: some of this is related to build data, which we will need to change after we add build data
  return otherAnswers;
};

const gatherOfferDataForSubmit = (allowIneligibleVehicleSubmission = false) => {
  // For now this is always true, but if we need to change this value based on appl activity, we
  // probably should move this to a store (offerOptions?) so we can have the interface change it
  // and be synced to the answer store
  const isConsumerOffer = true;
  const { mileage } = answerState.vehicle;
  const vehicleBuildDataFlags = {
    cadsUsed: answerState.vehicle.cadsUsed,
    transmissionsVinVerified: answerState.vehicle.transmissionsVinVerified,
    enginesVinVerified: answerState.vehicle.enginesVinVerified,
    drivetrainsVinVerified: answerState.vehicle.drivetrainsVinVerified,
    optionsVinVerified: answerState.vehicle.optionsVinVerified,
    colorsVinVerified: answerState.vehicle.colorsVinVerified,
  };

  const contactInfo = { ...answerState.contactInfo, confirmEmail: answerState.contactInfo.email };

  // Add required answers with default answers here

  // Should be const but had to rework to be able to reassign
  // TODO: Clean up rvAnswers
  let rvAnswers = [...cleanAnswersForAPI(answerState.replacementVehicle)];

  rvAnswers = removeInvalidAnswers(supplementState.rvLookingForOptions, rvAnswers);
  // Decode makeModelId into a make and model description. This might required for reporting purposes?
  const makeModelId = answerState.replacementVehicle[QUESTION_MAKE_MODEL]?.value;
  if (makeModelId) {
    const makeModel = supplementState.makesModels.values
      .reduce(
        (list, make) => [
          ...list,
          ...make.values.map((model) => ({
            id: model.id,
            make: make.label,
            model: model.label,
          })),
        ],
        []
      )
      .find((mm) => mm.id === makeModelId);

    rvAnswers.push(
      {
        questionId: QUESTION_MAKE,
        value: makeModel?.make,
        type: 'String',
      },
      {
        questionId: QUESTION_MODEL,
        value: makeModel?.model,
        type: 'String',
      }
    );
  }

  // If the customer is including replacement vehicle info, these questions are required by the API but
  // not ones that we currently want to ask.. so go and just set them to default values before we submit
  // Ideally we'd update the API to not require these and we could remove this code.
  if (answerState.replacementVehicle[QUESTION_LEAD_TYPE]?.value === ANSWER_LEAD_TYPE_OPT_IN) {
    rvAnswers.push(
      {
        questionId: 'qrv/AnyBodyStyle',
        value: true,
        type: undefined,
      },
      {
        questionId: 'qrv/TimeFrame',
        value: 'timeFrame/0',
        type: undefined,
      },
      {
        questionId: 'qrv/CreditScore',
        value: 'creditScore/0',
        type: undefined,
      }
    );
  }

  // TODO: clean up answers for cash offer
  if (
    answerState.replacementVehicle[QUESTION_LEAD_TYPE]?.value === ANSWER_LEAD_TYPE_CASH_OFFER ||
    answerState.replacementVehicle[QUESTION_LEAD_TYPE]?.value === ANSWER_LEAD_TYPE_OPT_OUT
  ) {
    const qIds = [QUESTION_YEAR_RANGE, QUESTION_NEW_CARS, QUESTION_CERTIFIED_CARS, QUESTION_USED_CARS, QUESTION_MAKE_MODEL, QUESTION_MAKE, QUESTION_MODEL];

    rvAnswers = rvAnswers.filter((q) => {
      if (!qIds.includes(q.questionId)) {
        return q;
      }
    });
  }

  const odorAnswer = answerState.conditions[QUESTION_ODORS];
  // Copy, as we don't want to remove the question from the actual store

  const conditionAnswers = { ...answerState.conditions };

  /* The service submission end-point expects a boolean true value when these questions are answered. The value we received from the document API call returns a string */
  for (const answerId in conditionAnswers) {
    if (conditionAnswers[answerId].type === 'SingleSelect') {
      conditionAnswers[answerId].value = conditionAnswers[answerId].value != false;
    }
  }

  // Remove QUESTION_TOGGLE_MODIFICATIONS from answers - not needed for submission
  delete conditionAnswers[QUESTION_TOGGLE_MODIFICATIONS];

  // Odor question needs to be submitted with history answers, though we want to ask it under the condition questions
  // So move it back to history and default to false if no value isn't set.
  delete conditionAnswers[QUESTION_ODORS];
  if (odorAnswer?.value === '') {
    odorAnswer.value = false;
  }

  const historyAnswers = [
    ...cleanAnswersForAPI({ ...answerState.history, [QUESTION_ODORS]: odorAnswer }),
    // These are old questions that we're not currently asking, but they are required by the API.. so go and just set them to default values before we submit
    // Ideally we'd update the API to not require these and we could remove this code.
    {
      questionId: 'qp/82', // Are you still making payments on this vehicle?
      value: false,
      type: 'Boolean',
    },
    {
      questionId: 'qp/76', // Has an insurance company paid for any repairs on your vehicle?
      value: false,
      type: 'Boolean',
    },
    {
      questionId: 'qp/17', // Do you have complete maintenance records?
      value: true,
      type: 'Boolean',
    },
  ];

  const apiOptions = [
    ...answerState.vehicle.apiSelectedOptionIds.map((optionId) => ({ optionId, isSelected: true })),
    ...answerState.vehicle.apiUnselectedOptionIds.map((optionId) => ({ optionId, isSelected: false })),
  ];

  return {
    prospect: {
      vehicle: {
        yearId: answerState.vehicle.yearId,
        makeId: answerState.vehicle.makeId,
        modelId: answerState.vehicle.modelId,
        trimId: answerState.vehicle.trimId,
        colorId: answerState.vehicle.colorId,
        transmissionId: answerState.vehicle.transmissionId,
        engineId: answerState.vehicle.engineId,
        drivetrainId: answerState.vehicle.drivetrainId,
        vin: answerState.vehicle.vin,
        // These fields are required by the API in that a valid value for the type is required. But we don't need the actual data to be sent.
        // Default to values that the API will accept if there are no values
        vehicleDescription: {
          year: answerState.vehicle.year ?? 0,
          make: answerState.vehicle.make ?? '',
          model: answerState.vehicle.model ?? '',
          trim: answerState.vehicle.trim ?? '',
          transmission: answerState.vehicle.transmission ?? '',
          engine: answerState.vehicle.engine ?? '',
          color: answerState.vehicle.color ?? '',
          drivetrain: answerState.vehicle.drivetrain ?? '',
          bodyStyle: answerState.vehicle.bodyStyle ?? '',
        },
      },
      options: apiOptions,
      cobranding: {
        groupId: answerState.dealerInfo.cobrandingGroupId ?? '',
      },
      mileage,
      contactInfo,
      isConsumerOffer,
      channelId: process.env.CHANNEL_ID,
      vehicleBuildDataFlags,
      dealerId: answerState.dealerInfo.dealerId,
      dealerRepId: answerState.dealerInfo.dealerRepId,
      dealerRepName: answerState.dealerInfo.dealerRepName,
      inviteId: answerState.dealerInfo.inviteId,
      ...(allowIneligibleVehicleSubmission ? { autoCheckData: answerState.other.autoCheckDataJSON.value } : ''),
    },
    mileage,
    contactInfo,
    tempComponentOfferImageGuid: imageCaptureState.imageGroupGuid ? imageCaptureState.imageGroupGuid : '',
    cadsBypass: !vehicleBuildDataFlags.cadsUsed,
    vinVerified: answerState.vehicle.vinVerified,
    isConsumerOffer,
    channelId: process.env.CHANNEL_ID,
    vehicleBuildDataFlags,
    bulkAnswers: [
      { canonicalName: 'history', answers: historyAnswers },
      { canonicalName: 'conditions', answers: cleanAnswersForAPI(conditionAnswers) },
      { canonicalName: 'other', answers: gatherOtherAnswerData() },
      { canonicalName: 'contactInfo', answers: contactInfo },
      { canonicalName: 'replacementVehicle', answers: removeTypes(rvAnswers) },
    ],
  };
};

const gatherAuthToken = () => {
  return answerState.dealerInfo.authToken;
};

export const submitOffer = (allowIneligibleVehicleSubmission = false) => {
  const authToken = gatherAuthToken();
  const data = gatherOfferDataForSubmit(allowIneligibleVehicleSubmission);
  const headers = {
    Authorization: `Basic ${process.env.ICO_TOKEN}`,
    QuestionConversion: 'fury', // TODO: Make this an env?
    Date: new Date().toUTCString(),
    'Content-Type': 'application/json',
  };
  if (authToken) {
    headers['authToken'] = authToken;
  }
  return fetch(`${process.env.BASE_URL}/${process.env.V_PREFIX}/combined-offers?a=b&api_key=${process.env.API_KEY}`, {
    headers,
    method: 'POST',
    mode: 'cors',
    redirect: 'follow',
    body: JSON.stringify(data),
  })
    .then((res) => jsonIf(res))
    .then((res) => {
      return res.data;
    });
};

export const updateCounteroffer = (offerGuid: string, amount: number) => {
  const isAccepted = amount === 0;
  return fetch(`${process.env.BASE_URL}/${process.env.V_PREFIX}/updateamount?a=b&api_key=${process.env.API_KEY}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      offerGuid,
      amount,
      isAccepted,
    }),
    method: 'PUT',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => {
      if (!res.ok) {
        throw res;
      }
      return res.status !== 204 && jsonIf(res);
    })
    .then((res) => res.data);
};

export const submitAnotherOffer = (disposalOptions: disposalOptions = null) => {
  const existingOfferId = disposalOptions.existingOfferId || disposalOptions.existingKbycOfferId;
  return new Promise((resolve) => {
    resolve({
      offerId: existingOfferId,
    });
  });
};

export const updateKbycAcceptOffer = (offerGuid: string) => {
  return fetch(`${process.env.BASE_URL}/${process.env.V_PREFIX}/disposal/acceptoffer?a=b&api_key=${process.env.API_KEY}`, {
    headers: {
      Authorization: `Basic ${process.env.ICO_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      offerGuid,
      dealerId: KBYC_DEALER.GUID,
    }),
    method: 'PUT',
    mode: 'cors',
    redirect: 'follow',
  })
    .then((res) => {
      if (!res.ok) {
        throw res;
      }
      return res.status !== 204 && jsonIf(res);
    })
    .then((res) => res.data);
};

export default { fetchInProgressOffer, fetchOfferDetail, submitOffer };
